import axios from 'axios';
import { take, takeEvery, call, put, select } from 'redux-saga/effects';
import { eventChannel, END } from 'redux-saga';

import { updateModel, createModelVersion } from '../../services';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import { calculateFileHash } from '../utils';
import {
  INIT_UPLOAD,
  uploadStarted,
  uploadFailed,
  uploadCompleted,
  uploadProgress,
} from './actions';
import { getUpload } from './selectors';

export function* doUpload(action) {
  const { id } = action;

  try {
    const { file, siteId, modelId, comment, rootFile, copyMappings, mappingsFromVersion } =
      yield select(getUpload, id);

    const hash = yield calculateFileHash(file);
    const modelData = { site: siteId };

    // update model's site in case it was created in team (with project instead of site)
    // and copy whatever value was previously set for arkitSupported
    const { arkitSupported } = yield call(updateModel, modelId, modelData);

    const outputs = [{ type: 'svf' }];

    if (arkitSupported) {
      outputs.push({ type: 'arkit' });
    }

    const versionData = {
      outputs,
      originalName: file.name,
      hash,
      comment,
      rootFile,
      copyMappings,
      mappingsFromVersion,
    };

    const {
      version: { id: versionId, status },
      url,
    } = yield call(createModelVersion, modelId, versionData);

    yield put(uploadStarted(id, versionId));

    if (status.toLowerCase() === 'failed') {
      yield put(uploadFailed(id));
      return;
    }

    const channel = eventChannel((emit) => {
      const fileSizeMb = file.size / (1024 * 1000);

      if (fileSizeMb > 75) {
        let resumableUrl;
        if (url.indexOf('?')) {
          resumableUrl = `${url.substr(0, url.indexOf('?'))}/resumable${url.substr(
            url.indexOf('?')
          )}`;
        } else {
          resumableUrl = `${url}/resumable`;
        }
        const chunkSize = 5 * 1024 * 1024; // Recommended size is 5 Mb
        const nbChunks = Math.ceil(file.size / chunkSize);
        const chunksMap = Array.from(
          {
            length: nbChunks,
          },
          (_e, i) => i
        );
        let progress = 0;

        chunksMap.forEach((chunkIdx) => {
          const start = chunkIdx * chunkSize;
          const end = Math.min(file.size, (chunkIdx + 1) * chunkSize) - 1;

          const range = `bytes ${start}-${end}/${file.size}`;
          const blob = file.slice(start, end + 1);
          axios({
            method: 'PUT',
            url: resumableUrl,
            headers: {
              'Content-Range': range,
              'Session-Id': hash,
            },
            data: blob,
          })
            .then(() => {
              if (chunkIdx === chunksMap.length - 1) {
                emit(uploadCompleted(id));
              } else {
                const currentChunk = chunkIdx * chunkSize;
                progress = progress > currentChunk ? progress : currentChunk;
                emit(uploadProgress(id, progress));
              }
            })
            .catch((e) => {
              console.error('Model upload error', e);
              emit(uploadFailed(id));
              emit(END);
            });
        });
      } else {
        // Non chunked upload
        const config = {
          onUploadProgress: ({ loaded }) => {
            emit(uploadProgress(id, loaded));
          },
        };

        axios
          .put(url, file, config)
          .then(() => {
            emit(uploadCompleted(id));
            emit(END);
          })
          .catch((e) => {
            console.error('Model upload error', e);
            emit(uploadFailed(id));
            emit(END);
          });
      }

      return () => {
        // clean up
      };
    });

    while (true) {
      const dispatchedAction = yield take(channel);
      yield put(dispatchedAction);
    }
  } catch (e) {
    console.error('Upload failed: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('uploadModelVersion', 'error')()));
    yield put(uploadFailed(id, e));
  }
}

export const sagas = [takeEvery(INIT_UPLOAD, doUpload)];
